import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IRoomNumberingMethodKey } from "@multiconsult-group/multiplacer-core";
import type { IRootState } from "../app/store";

export type IRoomNumberingRestrictionLine = {
  id: string;
  start: [number, number];
  end: [number, number];
};

type IRoomNumberingSliceState = {
  selectedMethodKey: IRoomNumberingMethodKey;
  heatmapVisible: boolean;
  restrictionLines: IRoomNumberingRestrictionLine[];
  selectedRestrictionLineId: string | null;
  restrictionLinePlacementMode: boolean;
  numberingOrigin: [number, number] | null;
  selectedBuildings: string[];
};

const initialState: IRoomNumberingSliceState = {
  selectedMethodKey: "NRH",
  heatmapVisible: false,
  restrictionLines: [],
  selectedRestrictionLineId: null,
  restrictionLinePlacementMode: false,
  numberingOrigin: [111989.18, 1216983.10],
  selectedBuildings: [],
};

export const roomNumberingSlice = createSlice({
  name: "roomNumbering",
  initialState,
  reducers: {
    setHeatmapVisible: (state, action: PayloadAction<boolean>) => {
      state.heatmapVisible = action.payload;
    },
    setSelectedRoomNumberingMethodKey: (state, action: PayloadAction<IRoomNumberingMethodKey>) => {
      state.selectedMethodKey = action.payload;
    },
    setNumberingOrigin: (state, action: PayloadAction<[number, number] | null>) => {
      state.numberingOrigin = action.payload;
    },
    setSelectedBuildings: (state, action: PayloadAction<string[]>) => {
      state.selectedBuildings = action.payload;
    },
    setRestrictionLines: (state, action: PayloadAction<IRoomNumberingRestrictionLine[]>) => {
      state.restrictionLines = action.payload;
      state.selectedRestrictionLineId = null;
    },
    addRestrictionLine: (state, action: PayloadAction<IRoomNumberingRestrictionLine>) => {
      state.restrictionLines.push(action.payload);
    },
    deleteRestrictionLine: (state, action: PayloadAction<string>) => {
      state.restrictionLines = state.restrictionLines.filter((restrictionLine) => restrictionLine.id !== action.payload);
      if (state.selectedRestrictionLineId === action.payload) state.selectedRestrictionLineId = null;
    },
    selectRestrictionLine: (state, action: PayloadAction<string | null>) => {
      state.selectedRestrictionLineId = action.payload;
    },
    toggleRestrictionLinePlacementMode: (state) => {
      state.restrictionLinePlacementMode = !state.restrictionLinePlacementMode;
      if (state.restrictionLinePlacementMode) state.selectedRestrictionLineId = null;
    },
  },
});

export const {
  setHeatmapVisible,
  setSelectedRoomNumberingMethodKey,
  setNumberingOrigin,
  setSelectedBuildings,
  setRestrictionLines,
  addRestrictionLine,
  deleteRestrictionLine,
  selectRestrictionLine,
  toggleRestrictionLinePlacementMode,
} = roomNumberingSlice.actions;

export const selectRoomNumberingHeatmapVisible = (state: IRootState) =>
  state.roomNumbering.heatmapVisible;

export const selectRoomNumberingSelectedMethodKey = (state: IRootState) =>
  state.roomNumbering.selectedMethodKey;

export const selectRoomNumberingRestrictionLines = (state: IRootState) =>
  state.roomNumbering.restrictionLines;

export const selectRoomNumberingSelectedRestrictionLineId = (state: IRootState) =>
  state.roomNumbering.selectedRestrictionLineId;

export const selectRoomNumberingRestrictionLinePlacementMode = (state: IRootState) =>
  state.roomNumbering.restrictionLinePlacementMode;

export const selectRoomNumberingNumberingOrigin = (state: IRootState) =>
  state.roomNumbering.numberingOrigin;

export const selectRoomNumberingSelectedBuildings = (state: IRootState) =>
  state.roomNumbering.selectedBuildings;
